/**
 * Asset manifest. Every film asset is outstanding at time of scaffolding, so
 * these paths point at files that do not exist yet — VideoPlaceholder renders a
 * labelled slot until they do. Dropping the delivered files in at these paths
 * is the whole integration step.
 *
 * See docs/OPEN_ITEMS.md for what is owed and by whom.
 *
 * Paths are base-aware so subdirectory deploys (e.g. /the-incomer-call/) work.
 */
import { publicPath } from './lib/publicPath';

/**
 * Cache key for the delivered film assets. Bump when one is replaced.
 *
 * These filenames are stable by design — a new cut of the instruction clip is
 * still instruction.mp4 — so the URL alone cannot tell a browser that the bytes
 * behind it changed. That was survivable until it met two other things: the
 * Netlify rule that served everything under /assets/ as `immutable` for a year,
 * and the SPA catch-all that answers a path with no file at it by returning
 * index.html at status 200 rather than a 404.
 *
 * Together they poisoned caches. A browser that asked for instruction.mp4
 * before the file was delivered got HTML, and cached that HTML as immutable
 * until 2027. Every visit since has read it back without asking the server,
 * failed to decode it, and fallen through to the placeholder slot — on a site
 * where the file has been live and correct for days.
 *
 * Fixing the header (see netlify.toml) stops NEW caches being poisoned; it
 * cannot clear the ones already out there, because `immutable` means the
 * browser never asks again. Only a different URL reaches those, which is what
 * this is for.
 */
const FILM_ASSET_VERSION = '2';

/** A delivered video, under a cache key we control. */
function video(name: string): string {
  return `${publicPath(`assets/video/${name}`)}?v=${FILM_ASSET_VERSION}`;
}

export const assets = {
  /** Siblings squawking + the line telling the guest what to do. */
  instruction: video('instruction.mp4'),
  /**
   * Looping character footage for the kiosk attract screen.
   *
   * Delivered 2026-09-16 and no longer standing in with the instruction clip.
   * Derived from `incomer-attract.mp4` by `npm run art` — the master is a
   * 37.6MB grade and arrived under public/, so see scripts/build-art.mjs for why
   * this path points at a derived file rather than the delivered one.
   *
   * AttractScreen plays it muted and looping, which is all it asks of a clip,
   * and the derive drops the audio track accordingly.
   */
  attractLoop: video('attract-loop.mp4'),
  /** The transformation transition. Spec unconfirmed — see TransitionOverlay. */
  transition: video('transition.mp4'),
  /** Title treatment — red on black; compositor screens out the black plate. */
  logo: publicPath('assets/img/Logo.png'),
} as const;

/** A derived image in the two formats `scripts/build-art.mjs` emits. */
export interface ArtImage {
  webp: string;
  /**
   * What a browser without WebP gets. JPEG for flat plates, PNG for anything
   * with a cut-out — a JPEG fallback on those would fill the transparency with
   * black and drop a rectangle over the sky.
   */
  fallback: string;
  /** Intrinsic width, so `<Artwork>` can build a width-descriptor srcset. */
  width: number;
}

function art(name: string, width: number): ArtImage {
  return {
    webp: publicPath(`assets/img/${name}.webp`),
    fallback: publicPath(`assets/img/${name}.jpg`),
    width,
  };
}

function cutout(name: string, width: number): ArtImage {
  return {
    webp: publicPath(`assets/img/${name}.webp`),
    fallback: publicPath(`assets/img/${name}.png`),
    width,
  };
}

/**
 * The photographic key art the storyboards are drawn on, delivered as three
 * separate layers so they can be parallaxed and composed per viewport rather
 * than baked into one flat plate.
 *
 * The background carries sky, sea and the distant islands. Its waterline sits at
 * 63.6% of its own height — measured off the plate, and the number the other two
 * layers are positioned against in TitleBackdrop.
 */
export const titleLayers = {
  bg: art('title-bg', 1600),
  /** Midground sea stacks, sitting on the waterline behind the cast. */
  rocks: cutout('title-rocks', 433),
  /** Gulls in flight, sitting between the stacks and the cast. */
  gulls: cutout('title-gulls', 698),
  /** The three leads in their wings, foreground. */
  cast: cutout('title-cast', 1080),
  /**
   * The campaign title lockup, keyed out of the one-sheet — see
   * scripts/build-art.mjs. Not the same mark as `assets.logo`, which is the
   * distressed treatment burned into the recorded clip.
   */
  wordmark: cutout('title-wordmark', 900),
} as const;

/** Where the background plate's horizon falls, as a fraction of its height. */
export const TITLE_WATERLINE = 0.636;

/**
 * The sky of the photographic title plate, sampled from it.
 *
 * Applied as an inline style rather than a Tailwind token on purpose. It began
 * life as a `sand` colour in theme.extend, and a newly added theme key is only
 * emitted once Tailwind reloads its config — which a long-running dev server
 * may not do. The class was in the markup and the rule was not in the
 * stylesheet, so the panel rendered with no background at all and looked like
 * the colour had simply been ignored. An inline style cannot fail that way.
 *
 * Distinct from `bone`, the painterly poster's cream: side by side the two read
 * as a mismatch, so anything on the title art uses this.
 */
export const TITLE_SKY = '#E8E1C4';

/**
 * Campaign key art, derived from the print masters by `npm run art`.
 *
 * Two sizes of the portrait ground rather than one: the kiosk's CSS viewport is
 * still UNVERIFIED (assumed 1080x1920 at DPR 2, see docs/KIOSK_HARDWARE.md), so
 * `<Artwork>` picks on devicePixelRatio. A 2160 variant is deliberately not
 * shipped — the unit has 4GB of RAM and is already running tracking, preview and
 * an encode, and a still illustration behind type survives being soft.
 */
export const artwork = {
  /** Clean Art, no type — the ground anything of ours sits on. Smallest first. */
  keyArt: [art('key-art-portrait-720', 720), art('key-art-portrait-1080', 1080)],
  /** 4x5 social crop, for the web end card. */
  endCardWeb: [art('end-card-web-1080', 1080)],
  /** Full one-sheet padded to 9:16 on the art's own cream. */
  endCardKiosk: [art('end-card-kiosk-1080', 1080)],
  /** 32px blur-up, inlined behind the key art while it decodes. */
  lqip: publicPath('assets/img/key-art-lqip.jpg'),
} as const;

export const links = {
  tickets: 'https://www.fandango.com/the-incomer-2026-246495/movie-overview',
  privacy: 'https://example.com/the-incomer/privacy',
} as const;

export const release = {
  /**
   * The whole release line, in one place. "Premieres" per client, 2026-09-15,
   * replacing "Starts" — note that the earlier wording was chosen because most
   * of the country opens the week after the 25th, so this line now points at a
   * single date more firmly than the release pattern does. The year because the
   * storyboards carry it. Written in sentence case; the display sites apply
   * `uppercase`.
   *
   * The SMS body carries the same wording and cannot import this file — it
   * reads the RELEASE_LINE secret, which has to be set to match. See
   * docs/OPEN_ITEMS.md.
   */
  line: 'Premieres September 25, 2026',
} as const;
